import { useState, useRef, useEffect } from "react";
import { useSWRConfig } from "swr";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faCircleCheck,
  faTriangleExclamation,
  faCircleQuestion,
  faFolderOpen,
  faBolt
} from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";

import BomReviewTable from "./BomReviewTable";
import BomConfirmationScreen from "./BomConfirmationScreen";
import BomTemplatesManager from "./BomTemplatesManager";
import { parseWorkbook } from "./workbookParser";
import { simulateExtraction } from "./extractionEngine";
import {
  buildFileFromExtraction,
  summarizeBatch,
  summarizeFile
} from "./mockBomData";
import {
  detectFormatKey,
  detectFormatFromWorkbook,
  findTemplate
} from "./bomTemplates";
import {
  startBomBulkUploadBatch,
  subscribeToBomBulkUploadTask,
  useBomTemplates
} from "../../../../helpers/bomBulkUpload";
import usePlatformLayoutContext from "../../../../helpers/contexts/platformLayout";

const supportedExtensions = ["xlsx", "xls", "zip", "csv"];

const isSupportedFile = ({ name }) => {
  const extension = name.split(".").pop().toLowerCase();
  return supportedExtensions.includes(extension);
};

const UploadDropzone = ({ onFilesSelected }) => {
  const { palette } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    return onFilesSelected([...event.dataTransfer.files]);
  };

  const lastChangeRef = useRef(0);

  const handleInputChange = (event) => {
    lastChangeRef.current = Date.now();
    const files = [...event.target.files];
    event.target.value = "";
    return onFilesSelected(files);
  };

  const openFilePicker = () => {
    if (Date.now() - lastChangeRef.current < 800) {
      return undefined;
    }
    return fileInputRef.current?.click();
  };

  const openFolderPicker = (event) => {
    event.stopPropagation();
    if (Date.now() - lastChangeRef.current < 800) {
      return undefined;
    }
    return folderInputRef.current?.click();
  };

  return (
    <>
      <Box
        onClick={openFilePicker}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 5,
          cursor: "pointer",
          border: `2px dashed ${
            isDragging ? palette.primary.main : palette.backgroundGray.dark
          }`,
          borderRadius: 2,
          backgroundColor: isDragging
            ? hexToRgba(palette.primary.main, 0.08)
            : palette.backgroundGray.light,
          transition: "all 0.15s ease"
        }}
      >
        <FontAwesomeIcon
          icon={faCloudArrowUp}
          size="2x"
          style={{ color: palette.primary.main }}
        />
        <Typography variant="subtitle1">
          Drag BOM files here, or click to browse
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Accepts .xlsx, .xls, .csv, or a .zip of many BOMs — upload now, review
          later
        </Typography>
        <Button
          size="small"
          startIcon={<FontAwesomeIcon icon={faFolderOpen} />}
          onClick={openFolderPicker}
        >
          Select an entire folder
        </Button>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleInputChange}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        webkitdirectory=""
        directory=""
        hidden
        onChange={handleInputChange}
      />
    </>
  );
};

const SummaryStat = ({ icon, colorKey, count, label }) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FontAwesomeIcon icon={icon} style={{ color: palette[colorKey].main }} />
      <Typography variant="subtitle2">
        {count} {label}
      </Typography>
    </Box>
  );
};

const BatchSummaryBanner = ({ summary }) => {
  const { palette } = useTheme();
  const { productCount, reportedCount, assumptionCount, insufficientCount } =
    summary;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
        p: 2,
        borderRadius: 1,
        backgroundColor: hexToRgba(palette.primary.main, 0.06),
        mb: 2
      }}
    >
      <Typography variant="subtitle1">{productCount} products parsed</Typography>
      <SummaryStat
        icon={faCircleCheck}
        colorKey="secondary"
        count={reportedCount}
        label="reported"
      />
      <SummaryStat
        icon={faTriangleExclamation}
        colorKey="warning"
        count={assumptionCount}
        label="assumption lines"
      />
      <SummaryStat
        icon={faCircleQuestion}
        colorKey="error"
        count={insufficientCount}
        label="insufficient"
      />
    </Box>
  );
};

const editLineWeightInBatch = (batch, sourceFile, lineIndex, weight) => ({
  ...batch,
  files: batch.files.map((file) => {
    if (file.sourceFile !== sourceFile) {
      return file;
    }

    const lineCount = file.lines.length;
    const isPackagingLine = lineIndex >= lineCount;
    const targetKey = isPackagingLine ? "packaging" : "lines";
    const targetIndex = isPackagingLine ? lineIndex - lineCount : lineIndex;

    const updatedLines = file[targetKey].map((line, index) =>
      index === targetIndex ? { ...line, weightGrams: weight } : line
    );

    return { ...file, [targetKey]: updatedLines };
  })
});

const SavedFormatsBanner = ({ count, onManage, onClear }) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: hexToRgba(palette.secondary.main, 0.08)
      }}
    >
      <FontAwesomeIcon icon={faBolt} style={{ color: palette.secondary.main }} />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {count} saved {count === 1 ? "format" : "formats"} — BOMs matching these
        parse instantly, no AI cost.
      </Typography>
      <Button size="small" color="inherit" onClick={onManage}>
        Manage
      </Button>
      <Button size="small" color="inherit" onClick={onClear}>
        Clear all
      </Button>
    </Box>
  );
};

const BomBulkUploadFlow = ({ onClose }) => {
  const { palette } = useTheme();
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [batch, setBatch] = useState(null);
  const [selectionError, setSelectionError] = useState(null);
  const [showOnlyNeedsAttention, setShowOnlyNeedsAttention] = useState(false);
  const [templates, , templateActions] = useBomTemplates();
  const { mutate } = useSWRConfig();
  const { activateSnackbar } = usePlatformLayoutContext();
  const [pendingFiles, setPendingFiles] = useState([]);
  const [unmappedFormats, setUnmappedFormats] = useState([]);
  const [mappingWorkbook, setMappingWorkbook] = useState(null);
  const [mappingExtraction, setMappingExtraction] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [imported, setImported] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [taskDocId, setTaskDocId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [importError, setImportError] = useState(null);
  const [showTemplatesManager, setShowTemplatesManager] = useState(false);
  const rawFilesRef = useRef([]);
  const parsedWorkbooksRef = useRef(new Map());
  const confirmedExtractionsRef = useRef(new Map());

  const formatKeyForFile = (file) => {
    const cached = parsedWorkbooksRef.current.get(file);
    return cached
      ? detectFormatFromWorkbook(cached, file.name)
      : detectFormatKey(file.name);
  };

  const openMapperForFormat = (formatKey, files) => {
    const example = files.find((file) => formatKeyForFile(file) === formatKey);
    const exampleWorkbook = example && parsedWorkbooksRef.current.get(example);
    setParseError(null);
    if (!example || !exampleWorkbook) {
      setMappingWorkbook(null);
      setMappingExtraction(null);
      setParseError(
        `Couldn't read ${
          example?.name || "the example file"
        }. It may be unsupported or corrupt.`
      );
      return;
    }
    setMappingWorkbook(exampleWorkbook);
    setMappingExtraction({ ...simulateExtraction(exampleWorkbook), formatKey });
  };

  const errorFileShape = (fileName) => ({
    sourceFile: fileName,
    detectedTemplate: "unreadable",
    parseConfidence: 0,
    status: "low-confidence",
    product: {
      sku: fileName,
      name: fileName,
      category: "apparel",
      subcategory: null,
      countryOfOrigin: null,
      productWeightGrams: null
    },
    lines: [],
    packaging: [],
    warnings: ["Could not read this file — unsupported or corrupt."]
  });

  const finishWithFiles = (files, knownTemplates = templates) => {
    rawFilesRef.current = files;
    const newQueue = files.map((file) => {
      const formatKey = formatKeyForFile(file);
      const matched = findTemplate(formatKey, knownTemplates);
      return {
        name: file.name,
        status: "needs-review",
        formatKey,
        matchedTemplateName: matched?.name || null
      };
    });
    setQueuedFiles(newQueue);

    const parsedFiles = files.map((file) => {
      const confirmed = confirmedExtractionsRef.current.get(file.name);
      if (confirmed) {
        return buildFileFromExtraction(confirmed);
      }
      const workbook = parsedWorkbooksRef.current.get(file);
      if (!workbook) {
        return errorFileShape(file.name);
      }
      const formatKey = formatKeyForFile(file);
      const matched = findTemplate(formatKey, knownTemplates);
      const overrides = matched?.materialTypeOverrides || {};
      return buildFileFromExtraction(simulateExtraction(workbook, { overrides }));
    });

    setBatch({
      batchId: "uploaded-batch",
      uploadedAt: "Just now",
      files: parsedFiles
    });
  };

  const handleFilesSelected = async (files) => {
    const supportedFiles = files.filter(isSupportedFile);

    if (!supportedFiles.length) {
      return setSelectionError(
        "No supported files found. Upload .xlsx, .xls, .csv, or a .zip of BOMs."
      );
    }

    setSelectionError(null);
    setIsParsing(true);

    await Promise.all(
      supportedFiles.map(async (file) => {
        const workbook = await parseWorkbook(file).catch(() => null);
        parsedWorkbooksRef.current.set(file, workbook);
      })
    );

    setIsParsing(false);

    const uniqueFormats = [
      ...new Set(supportedFiles.map((file) => formatKeyForFile(file)))
    ];
    const needMapping = uniqueFormats.filter(
      (formatKey) => !findTemplate(formatKey, templates)
    );

    if (needMapping.length) {
      setPendingFiles(supportedFiles);
      setUnmappedFormats(needMapping);
      return openMapperForFormat(needMapping[0], supportedFiles);
    }

    return finishWithFiles(supportedFiles);
  };

  const handleSaveTemplate = async (template) => {
    const id = `tmpl-${template.formatKey}`;
    const { confirmedExtraction, ...templateToSave } = template;
    const fullTemplate = { ...templateToSave, id };
    if (confirmedExtraction?.fileName) {
      confirmedExtractionsRef.current.set(
        confirmedExtraction.fileName,
        confirmedExtraction
      );
    }
    const saveResult = await templateActions.saveTemplate(fullTemplate);
    if (!saveResult?.success) {
      activateSnackbar({
        message:
          "Couldn't save this format — your mapping wasn't persisted. Please retry.",
        severity: "error"
      });
      return null;
    }
    const remaining = unmappedFormats.slice(1);
    setUnmappedFormats(remaining);
    setMappingWorkbook(null);
    setMappingExtraction(null);
    if (remaining.length) {
      return openMapperForFormat(remaining[0], pendingFiles);
    }
    const knownTemplates = [
      ...templates.filter((t) => t.id !== id),
      fullTemplate
    ];
    return finishWithFiles(pendingFiles, knownTemplates);
  };

  const handleCancelMapping = () => {
    setUnmappedFormats([]);
    setPendingFiles([]);
    setMappingWorkbook(null);
    setMappingExtraction(null);
    setParseError(null);
  };

  const fileNeedsAttention = (file) =>
    file.status === "low-confidence" || summarizeFile(file).insufficientCount > 0;

  const handleEditLineWeight = (sourceFile, lineIndex, weight) =>
    setBatch((current) =>
      editLineWeightInBatch(current, sourceFile, lineIndex, weight)
    );

  const handleImport = async () => {
    setImportError(null);
    const companyId = window.sessionStorage.getItem("accountId");
    const rawFiles = rawFilesRef.current;
    if (!companyId || !rawFiles.length) {
      setImportError(
        "Couldn't start import — missing account or files cleared from session."
      );
      return;
    }

    const confirmedExtractions = Object.fromEntries(
      [...confirmedExtractionsRef.current.entries()].filter(([fileName]) =>
        rawFiles.some((file) => file.name === fileName)
      )
    );

    const formatKeysByFileName = Object.fromEntries(
      rawFiles.map((file) => [file.name, formatKeyForFile(file)])
    );

    const result = await startBomBulkUploadBatch({
      companyId,
      files: rawFiles,
      formatKeysByFileName,
      confirmedExtractions
    }).catch((err) => ({ error: err?.message || "upload-failed" }));

    if (result.error || !result.taskDocId) {
      setImportError(
        result.error && typeof result.error === "string"
          ? `Backend import failed — ${result.error}`
          : "Backend import failed — nothing was saved. Please retry or contact support."
      );
      return;
    }

    setTaskDocId(result.taskDocId);
    setImported(true);
  };

  useEffect(() => {
    if (!taskDocId) {
      return undefined;
    }
    const companyId = window.sessionStorage.getItem("accountId");
    if (!companyId) {
      return undefined;
    }
    return subscribeToBomBulkUploadTask({
      companyId,
      taskDocId,
      onUpdate: (next) => {
        setTaskStatus(next);
        const isTerminal =
          next?.status === "finished" ||
          next?.status === "error" ||
          next?.status === "paused";
        if (isTerminal) {
          mutate("product-components");
          mutate("product-lifecycle-events");
        }
      }
    });
  }, [taskDocId, mutate]);

  const summary = batch ? summarizeBatch(batch) : null;
  const needsAttentionCount = batch
    ? batch.files.filter(fileNeedsAttention).length
    : 0;
  const displayedFiles = batch
    ? batch.files.filter(
        (file) => !showOnlyNeedsAttention || fileNeedsAttention(file)
      )
    : [];

  const activeMappingFormat = unmappedFormats[0] || null;
  const exampleFileForFormat = activeMappingFormat
    ? pendingFiles.find(
        (file) => detectFormatKey(file.name) === activeMappingFormat
      )?.name
    : null;

  const reusedTemplateNames = [
    ...new Set(queuedFiles.map((file) => file.matchedTemplateName).filter(Boolean))
  ];

  const resetForMoreUploads = () => {
    setBatch(null);
    setQueuedFiles([]);
    setImported(false);
  };

  if (imported && summary) {
    const productStatusCounts = batch.files.reduce(
      (acc, file) => {
        const fileSummary = summarizeFile(file);
        const key =
          fileSummary.insufficientCount > 0
            ? "needsData"
            : fileSummary.assumptionCount > 0
            ? "estimated"
            : "complete";
        return { ...acc, [key]: acc[key] + 1 };
      },
      { complete: 0, estimated: 0, needsData: 0 }
    );

    return (
      <Box sx={{ maxWidth: 760, mx: "auto", py: 5, textAlign: "center" }}>
        <FontAwesomeIcon
          icon={faCircleCheck}
          size="3x"
          style={{ color: palette.secondary.main }}
        />
        <Typography variant="h5" sx={{ mt: 2 }}>
          {summary.productCount} products added to your catalog
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
          Everything imported — including items with assumptions or missing data.
          They&apos;re flagged in Products so you or your customer can accept
          assumptions and fill gaps anytime.
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 3,
            my: 3
          }}
        >
          <SummaryStat
            icon={faCircleCheck}
            colorKey="secondary"
            count={productStatusCounts.complete}
            label="complete"
          />
          <SummaryStat
            icon={faTriangleExclamation}
            colorKey="warning"
            count={productStatusCounts.estimated}
            label="with assumptions"
          />
          <SummaryStat
            icon={faCircleQuestion}
            colorKey="error"
            count={productStatusCounts.needsData}
            label="need data"
          />
        </Box>
        {importError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {importError}
          </Alert>
        )}
        {taskDocId && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: hexToRgba(palette.primary.light, 0.1),
              borderRadius: 1,
              textAlign: "left"
            }}
          >
            <Typography variant="subtitle2" color="textPrimary">
              Backend import status
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {taskStatus
                ? `${taskStatus.status} — ${taskStatus.totalTasksProcessed || 0} imported${
                    taskStatus.totalTasksFailed
                      ? `, ${taskStatus.totalTasksFailed} failed`
                      : ""
                  } (${taskStatus.totalTasks || "?"} total)`
                : "Starting…"}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <Button color="inherit" onClick={resetForMoreUploads}>
            Upload more
          </Button>
          <Button variant="contained" color="secondary" onClick={onClose}>
            Done
          </Button>
        </Box>
      </Box>
    );
  }

  if (activeMappingFormat) {
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", py: 3 }}>
        {parseError && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button color="inherit" onClick={handleCancelMapping}>
                Close
              </Button>
            </Box>
          </>
        )}
        {!parseError && mappingWorkbook && mappingExtraction && (
          <BomConfirmationScreen
            extraction={mappingExtraction}
            onConfirm={handleSaveTemplate}
            onCancel={handleCancelMapping}
          />
        )}
        {!parseError && !mappingWorkbook && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 6
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Reading {exampleFileForFormat || "your file"}…
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bulk Upload BOMs
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Upload many supplier BOMs at once. New formats are mapped once and saved;
        formats we recognize are parsed instantly with no AI cost.
      </Typography>

      {templateActions.error && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={templateActions.refetch}>
              Retry
            </Button>
          }
        >
          Couldn&apos;t load your saved formats. They&apos;re still on the
          server — please retry before uploading.
        </Alert>
      )}

      {!!templates.length && (
        <SavedFormatsBanner
          count={templates.length}
          onManage={() => setShowTemplatesManager(true)}
          onClear={async () => {
            const result = await templateActions.clearAllTemplates();
            if (!result?.success) {
              activateSnackbar({
                message: `Couldn't clear ${result.failedCount} format(s). Please retry.`,
                severity: "error"
              });
            }
          }}
        />
      )}

      <BomTemplatesManager
        open={showTemplatesManager}
        onClose={() => setShowTemplatesManager(false)}
        templates={templates}
        onSaveTemplate={async (template) => {
          const result = await templateActions.saveTemplate(template);
          if (!result?.success) {
            activateSnackbar({
              message: "Couldn't save changes to this format. Please retry.",
              severity: "error"
            });
          }
          return result;
        }}
        onDeleteTemplate={async (template) => {
          const result = await templateActions.deleteTemplate(template.id);
          if (!result?.success) {
            activateSnackbar({
              message: "Couldn't delete this format. Please retry.",
              severity: "error"
            });
          }
          return result;
        }}
      />


      <Box sx={{ my: 3 }}>
        <UploadDropzone onFilesSelected={handleFilesSelected} />
        {selectionError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {selectionError}
          </Alert>
        )}
      </Box>

      {isParsing && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 4
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            Reading your BOMs and extracting materials…
          </Typography>
        </Box>
      )}


      {batch && (
        <>
          {!!reusedTemplateNames.length && (
            <Alert
              severity="success"
              icon={<FontAwesomeIcon icon={faBolt} />}
              sx={{ mb: 2 }}
            >
              Parsed using saved {reusedTemplateNames.length === 1 ? "format" : "formats"}:{" "}
              {reusedTemplateNames.join(", ")} — no AI needed.
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Prototype: product names come from your real filenames, but the
            parsed materials are simulated. Yellow lines used an assumed weight;
            red lines need attention before import.
          </Alert>
          <BatchSummaryBanner summary={summary} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1
            }}
          >
            <Typography variant="overline" color="textSecondary">
              {showOnlyNeedsAttention
                ? `${displayedFiles.length} products needing attention`
                : `${displayedFiles.length} products`}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyNeedsAttention}
                  onChange={(event) =>
                    setShowOnlyNeedsAttention(event.target.checked)
                  }
                />
              }
              label={`Show only products needing attention (${needsAttentionCount})`}
            />
          </Box>
          <BomReviewTable
            files={displayedFiles}
            onEditLineWeight={handleEditLineWeight}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Everything imports — including assumption and needs-attention lines.
            Products land in your catalog flagged, so you or your customer can
            accept assumptions or add missing data anytime.
          </Alert>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            <Button color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleImport}
            >
              Import all {summary.productCount} products
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BomBulkUploadFlow;
